import { useState } from "react"

import LoadingOverlay from "@p-ban/common/src/components/overlay/LoadingOverlay/LoadingOverlay"
import useMountEffect from "@p-ban/common/src/hooks/useMountEffect"
import SEMINAR_STATUS from "@p-ban/core/src/seminar/constant/SEMINAR_STATUS"

import { RequiredAuth } from "../../../components/logic/RequiredAuth/RequiredAuth"
import MyPageLayout from "../../../layout/MyPage/MyPageLayout"
import seminarApi from "../api/seminarApi"
import type { SeminarValues } from "../api/seminarApi"
import { SeminarEntryFormContext } from "../context/SeminarEntryFormContext"
import type { SeminarEntryFormValues } from "../types/SeminarEntryFormValues"
import SeminarEntryInvalid from "./SeminarEntryInvalid"

const SeminarFormProvider = ({ children }: { children: React.ReactNode }) => {
  const [seminar, setSeminar] = useState<SeminarValues>()

  const [seminarEntryForm, setSeminarEntryForm] = useState<SeminarEntryFormValues>({
    personal: null,
    familyName: "",
    firstName: "",
    familyNameKana: "",
    firstNameKana: "",
    company: "",
    department: "",
    email: "",
    telNo: "",
    requestContents: [],
    requestMessage: "",
    cognitiveMedia: null,
    cognitiveMediaOther: "",
    participateIndividualConsultations: null,
    consultation: "",
  })

  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const [isInvalid, setIsInvalid] = useState(false)

  useMountEffect(() => {
    // セミナー情報取得
    const semid = new URLSearchParams(window.location.search).get("semid") ?? ""
    if (semid === "") {
      setIsInvalid(true)
      return
    }
    seminarApi.getSeminar(semid).then((result) => {
      if (result.isSuccess) {
        setSeminar(result.data)
        if (!SEMINAR_STATUS[result.data.seminarStatus].isAccepting) {
          setIsInvalid(true)
        }
      } else {
        setIsInvalid(true)
      }
    })

    // ユーザ情報の取得
    seminarApi.getEntryUser().then((getEntryUserResult) => {
      if (getEntryUserResult.isError) {
        alert(getEntryUserResult.data.message)
        return
      }

      const entryUserResultData = getEntryUserResult.data
      setIsLoggedIn(entryUserResultData.isLoggedIn)
      if (entryUserResultData.isLoggedIn) {
        setSeminarEntryForm((prev) => ({
          ...prev,
          personal: entryUserResultData.personal,
          familyName: entryUserResultData.familyName,
          firstName: entryUserResultData.firstName,
          familyNameKana: entryUserResultData.familyNameKana ?? "",
          firstNameKana: entryUserResultData.firstNameKana ?? "",
          company: entryUserResultData.company ?? "",
          department: entryUserResultData.department ?? "",
          email: entryUserResultData.email,
          telNo: entryUserResultData.telNo ?? "",
        }))
      }
    })
  })

  if (isInvalid) {
    return (
      <MyPageLayout layout={isLoggedIn ? "full" : "simple"}>
        <SeminarEntryInvalid seminar={seminar} />
      </MyPageLayout>
    )
  }
  if (seminar === undefined) {
    return <LoadingOverlay theme="light" />
  }

  return (
    <RequiredAuth enabled={seminar.accountRequired} fallback={<LoadingOverlay theme="light" />}>
      <SeminarEntryFormContext.Provider value={{ seminar, seminarEntryForm, setSeminarEntryForm, isLoggedIn }}>
        {children}
      </SeminarEntryFormContext.Provider>
    </RequiredAuth>
  )
}

export default SeminarFormProvider
